import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, HelpCircle, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FAQ_DATA = [
  {
    id: 'what-is-okiescape',
    q: 'What is Oki Escape exactly?',
    qJa: 'OKI-ESCAPEとは何ですか？',
    a: 'Oki Escape raises the stakes and excitement to reinvigorate players\' love of games in a real-life experience. You and your friends can sail on the Pirate Ship, plan a bank heist, or survive a demolition. Once you\'ve successfully escaped one room, join our email list to receive notification of new rooms as they come to life!',
    aJa: 'OKI-ESCAPEは、リアルな体験でゲームへの愛を再燃させるエンターテインメントです。海賊船での冒険、銀行強盗の計画、または爆破からの脱出が楽しめます。脱出に成功したら、メールリストに登録して新しい部屋の通知を受け取りましょう！',
  },
  {
    id: 'what-is-escape-room',
    q: 'What is an "Escape Room"?',
    qJa: '「脱出ゲーム」とは何ですか？',
    a: 'What sets escape rooms apart from other real-life gaming experiences is that players are in control of the outcome of the game. Simply put, escape rooms are designed to lock players inside a room where they\'ll have to race against the clock to uncover secrets and clues to help them escape. It is up to the players to use their observation and logic to uncover the room\'s secrets and earn their freedom before time runs out.',
    aJa: '脱出ゲームは、プレイヤーがゲームの結果をコントロールできるリアルなゲーム体験です。部屋に閉じ込められ、制限時間内に秘密や手がかりを見つけて脱出しなければなりません。観察力と論理力を使って、時間切れになる前に自由を勝ち取りましょう。',
  },
  {
    id: 'how-do-they-work',
    q: 'How do escape rooms work?',
    qJa: '脱出ゲームはどのように進みますか？',
    a: 'Teams will have to work together and combine their wits to successfully free themselves before the 60-minute clock strikes zero. While you will essentially be locked inside a room, know that you may ask to be let out if you or anyone else feels uncomfortable for any reason during the course of the game. Your safety and comfort are among our highest priorities.',
    aJa: 'チームで協力して、60分の制限時間内に脱出しましょう。部屋に閉じ込められますが、ゲーム中に不快に感じた場合はいつでも退出をリクエストできます。お客様の安全と快適さは最優先事項です。',
  },
  {
    id: 'how-to-get-out',
    q: 'How do you get out of an escape room?',
    qJa: '脱出ゲームからどうやって出ますか？',
    a: 'Once locked in the room, you will have 60 minutes to search for clues and solve puzzles, hopefully leading to your escape. It\'s up to you and your friends to crack our codes in time and find the correct escape route.',
    aJa: '部屋に閉じ込められたら、60分間で手がかりを探し、パズルを解いて脱出を目指します。仲間と一緒にコードを解読し、正しい脱出ルートを見つけましょう。',
  },
  {
    id: 'how-to-play',
    q: 'How can I play Oki Escape?',
    qJa: 'OKI-ESCAPEはどうやって遊べますか？',
    a: 'Gather your friends, family, or coworkers and book the room of your choice. Each room has a wholly immersive atmosphere and story to set the stage for your adventure. Escape rooms are the perfect chance to put your puzzle-solving skills to the test and have fun!',
    aJa: '友達、家族、同僚を集めて、お好きな部屋を予約してください。各部屋には没入感のある雰囲気とストーリーがあり、冒険の舞台を演出します。パズル解きのスキルを試す絶好のチャンスです！',
  },
  {
    id: 'private-groups',
    q: 'What if I only want my group in an escape room?',
    qJa: '自分たちのグループだけで遊べますか？',
    a: 'That\'s simple! We only allow you to play with your individual groups of friends, co-workers, fire-teams, and family. Your experience is always private.',
    aJa: 'もちろん！OKI-ESCAPEでは、お友達、同僚、仲間、ご家族のグループだけでプレイしていただけます。体験は常にプライベートです。',
  },
  {
    id: 'larger-groups',
    q: 'What if we want a larger group than the maximum?',
    qJa: '最大人数より多い場合はどうすればいいですか？',
    a: 'We highly recommend groups of 8 or smaller to better enjoy the experience. However, Pirates has seen larger groups, and our Oceans 13 room at Rycom Mall can fit up to 15 guests. Demolition Abduction is for up to 4. Message us on Facebook or contact us to discuss your group size.\n\n• Pirates: Up to 8\n• Oceans 13 (Bank Heist): Up to 15\n• Demolition Abduction: Up to 4',
    aJa: '8人以下のグループがおすすめですが、パイレーツはそれ以上のグループも対応可能で、ライカムモールのOceans 13は最大15名まで収容できます。Demolition Abductionは最大4名です。Facebookまたはお問い合わせページからグループサイズについてご相談ください。\n\n• パイレーツ：最大8名\n• Oceans 13（銀行強盗）：最大15名\n• Demolition Abduction：最大4名',
  },
  {
    id: 'age-limit',
    q: 'Is there an age limit for any of your rooms?',
    qJa: '年齢制限はありますか？',
    a: 'Though all of our puzzles are designed for adult minds, we welcome all ages. However, if any member of your party is under 13 years of age, there must be at least one guardian to accompany them. Staff always has the right to request a guardian based on group size and ages.',
    aJa: 'パズルは大人向けに設計されていますが、全年齢を歓迎しています。ただし、13歳未満のお子様には保護者の同伴が必要です。スタッフはグループのサイズと年齢に基づいて保護者の同伴を要請する権利があります。',
  },
  {
    id: 'original-escape-room',
    q: 'Is this the original escape room in Okinawa?',
    qJa: '沖縄初の脱出ゲームですか？',
    a: 'Yes! Oki Escape is the very first escape room in Okinawa and the very first American-owned escape room in Japan.',
    aJa: 'はい！OKI-ESCAPEは沖縄初の脱出ゲームであり、日本初のアメリカ人オーナーによる脱出ゲームです。',
  },
  {
    id: 'parking',
    q: 'Is there parking?',
    qJa: '駐車場はありますか？',
    a: 'Yes! For our Rycom Mall location (Oceans 13 & Demolition Abduction), park on the 5th floor for direct access. On rainy days, park inside and take the escalators up. For our Pirates location in Okinawa City, we recommend the Center Parking Lot nearby. Check the Directions page for details.',
    aJa: 'はい！ライカムモール（Oceans 13 & Demolition Abduction）は5階駐車場が直接アクセスに便利です。雨の日は館内に駐車してエスカレーターをご利用ください。沖縄市のパイレーツは近くのセンター駐車場がおすすめです。詳しくはアクセスページをご覧ください。',
  },
  {
    id: 'return-games',
    q: 'What if we can\'t escape in time?',
    qJa: '時間内に脱出できなかった場合は？',
    a: 'No worries! You can save your place and return to finish where you left off. Return games are only ¥1,000 for the entire hour — the first escape room we\'ve seen offer this special! Ask for more time or come back refreshed.',
    aJa: '心配いりません！セーブして、次回途中から再開できます。リターンゲームは1時間たったの¥1,000です。もっと時間を求めるか、リフレッシュしてまた来てください！',
  },
  {
    id: 'cancellation',
    q: 'What if I need to cancel?',
    qJa: 'キャンセルが必要な場合は？',
    a: 'We work with everyone on special circumstances. Please cancel at least 3 days before your appointment, or you may be charged a cancellation fee of ¥9,000. Reschedules outside of 3 days are completely FREE. Last-minute cancellations or no-shows on the day will also incur the minimum booking fee. Please message us ASAP for specific circumstances — we\'re a family-run business and happy to help!',
    aJa: '特別な事情にはできる限り対応いたします。予約日の3日前までにキャンセルしてください。3日以内のキャンセルは¥9,000のキャンセル料が発生する場合があります。3日以上前の日程変更は完全無料です。当日のキャンセルや無断キャンセルにも最低予約料金が発生します。できるだけ早くご連絡ください — 家族経営ですので、喜んでお手伝いします！',
  },
];

export default function FAQPage() {
  const { lang } = useLanguage();
  const isJa = lang === 'ja';
  const [openId, setOpenId] = useState(null);
  const [roomSettings, setRoomSettings] = useState({ oceans13: true, demolition: true, pirate: true, sweet_escape: false });

  useEffect(() => {
    axios.get(`${API}/api/settings/rooms`).then(r => setRoomSettings(r.data)).catch(() => {});
  }, []);

  const toggle = (id) => setOpenId(prev => prev === id ? null : id);

  // Build the group sizes list dynamically based on visible rooms
  const buildGroupSizes = () => {
    const lines = [];
    if (roomSettings.pirate) lines.push(isJa ? '• パイレーツ：最大8名' : '• Pirates: Up to 8');
    if (roomSettings.oceans13) lines.push(isJa ? '• Oceans 13（銀行強盗）：最大15名' : '• Oceans 13 (Bank Heist): Up to 15');
    if (roomSettings.demolition) lines.push(isJa ? '• Demolition Abduction：最大4名' : '• Demolition Abduction: Up to 4');
    if (roomSettings.sweet_escape) lines.push(isJa ? '• Bake Me Happy：2〜6名' : '• Bake Me Happy: 2–6');
    return lines.join('\n');
  };

  const faqItems = FAQ_DATA.map(item => {
    if (item.id === 'larger-groups') {
      const sizes = buildGroupSizes();
      const baseEn = 'We highly recommend groups of 8 or smaller to better enjoy the experience. However, Pirates has seen larger groups, and our Oceans 13 room at Rycom Mall can fit up to 15 guests. Demolition Abduction is for up to 4. Message us on Facebook or contact us to discuss your group size.';
      const baseJa = '8人以下のグループがおすすめですが、パイレーツはそれ以上のグループも対応可能で、ライカムモールのOceans 13は最大15名まで収容できます。Demolition Abductionは最大4名です。Facebookまたはお問い合わせページからグループサイズについてご相談ください。';
      return {
        ...item,
        a: baseEn + (sizes ? '\n\n' + sizes : ''),
        aJa: baseJa + (sizes ? '\n\n' + sizes : ''),
      };
    }
    return item;
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <div className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link
            to="/"
            data-testid="faq-back-link"
            className="flex items-center gap-2 text-muted-foreground hover:text-brand font-heading text-xs tracking-widest uppercase transition-colors"
          >
            <ArrowLeft size={14} /> {isJa ? 'ホームに戻る' : 'Back to OkiEscape'}
          </Link>
          <Link
            to="/directions"
            data-testid="faq-directions-link"
            className="text-muted-foreground hover:text-brand font-heading text-xs tracking-widest uppercase transition-colors"
          >
            {isJa ? 'アクセス' : 'Directions'}
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand/10 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-12 pb-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 text-brand font-heading text-xs tracking-[0.25em] uppercase mb-4">
            <HelpCircle size={14} />
            <span>{isJa ? 'よくある質問' : 'Frequently Asked Questions'}</span>
          </div>
          <h1
            data-testid="faq-heading"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-wide uppercase"
            style={{ textShadow: '0 0 40px rgba(227,37,16,0.15)' }}
          >
            {isJa ? 'よくある質問' : 'FAQs'}
          </h1>
          <p className="font-sans text-base text-muted-foreground mt-3 max-w-xl mx-auto">
            {isJa
              ? 'OKI-ESCAPEについて知っておくべきことすべて'
              : 'Everything you need to know about OKI-ESCAPE'}
          </p>
        </div>
      </div>

      {/* FAQ list */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-10">
        <div className="space-y-3">
          {faqItems.map(item => (
            <FAQItem key={item.id} item={item} isJa={isJa} isOpen={openId === item.id} onToggle={() => toggle(item.id)} />
          ))}
        </div>
      </div>

      {/* Cancellation policy highlight */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <div
          data-testid="cancellation-policy-box"
          className="border-2 border-yellow-600/40 bg-yellow-500/5 rounded-2xl p-6"
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-yellow-500" />
            <h3 className="font-heading text-lg tracking-wider uppercase text-yellow-500">
              {isJa ? 'キャンセルポリシー' : 'Cancellation Policy'}
            </h3>
          </div>
          <div className="font-sans text-sm text-foreground/80 space-y-2">
            <p>{isJa
              ? 'キャンセルと日程変更は異なります。予約日の3日前までにキャンセルしてください。3日以内のキャンセルはデポジットが没収されます。'
              : 'Cancelling and rescheduling are not the same. Please cancel at least 3 days before your appointment. Cancellations within 3 days will lose the deposit.'}
            </p>
            <p>{isJa
              ? '3日以上前の日程変更は完全無料です。当日のキャンセル・無断キャンセルには最低予約料金が発生します。'
              : 'Reschedules outside of 3 days are completely FREE. Last-minute cancellations or no-shows the day of will incur the minimum booking fee.'}
            </p>
            <p className="text-muted-foreground italic">
              {isJa
                ? '事情がある場合はお早めにご連絡ください。家族経営ですので、日程変更のお手伝いをいたします。'
                : 'We understand things happen — we\'re a family-run business. Please message us as early as possible and we\'ll work together on a reschedule.'}
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="border-t border-border bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 text-center">
          <p className="font-heading text-xl tracking-wider uppercase mb-2">
            {isJa ? '質問がまだありますか？' : 'Still have questions?'}
          </p>
          <p className="font-sans text-sm text-muted-foreground mb-5">
            {isJa ? 'お気軽にお問い合わせください' : 'Don\'t hesitate to contact us — we\'re happy to help'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:info@okiescape.com"
              data-testid="faq-contact-email"
              className="font-heading text-sm tracking-widest uppercase bg-brand text-white px-6 py-3 rounded-xl hover:bg-brand-hover transition-colors"
            >
              {isJa ? 'メールする' : 'Contact Us'}
            </a>
            <a
              href="https://book.squareup.com/appointments/h2vlbju85siksv/location/L7GDDBBGP4SJJ/services"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-book-now"
              className="font-heading text-sm tracking-widest uppercase border-2 border-brand text-brand px-6 py-3 rounded-xl hover:bg-brand hover:text-white transition-colors"
            >
              {isJa ? '今すぐ予約' : 'Book Now'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ item, isJa, isOpen, onToggle }) {
  return (
    <div
      data-testid={`faq-item-${item.id}`}
      className={`rounded-xl border transition-colors ${isOpen ? 'border-brand/40 bg-brand/5' : 'border-border bg-card hover:border-border/80'}`}
    >
      <button
        data-testid={`faq-toggle-${item.id}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="font-heading text-base sm:text-lg tracking-wide uppercase">{isJa ? item.qJa : item.q}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180 text-brand' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-5">
          <div className="border-t border-border/50 pt-4">
            <p className="font-sans text-sm text-foreground/85 leading-relaxed whitespace-pre-line">
              {isJa ? item.aJa : item.a}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
